import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ObjektStrukturTreetableService, TreeNodeData } from '../../services/objekt-struktur-treetable.service';
import { ObjektStrukturService } from '../../services/objekt-struktur.service';
import { StandortService } from '../../services/standort.service';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JournalService } from '../../@core/journal.service';
import { AddGebaeudeDialogComponent } from './add-gebaeude-dialog/add-gebaeude-dialog.component';
import { AddSektionDialogComponent } from './add-sektion-dialog/add-sektion-dialog.component';
import { AddRaumDialogComponent } from './add-raum-dialog/add-raum-dialog.component';
import { AddIndicatorDialogComponent } from './add-indicator-dialog/add-indicator-dialog.component';
import { EditGebaeudeDialogComponent } from './edit-gebaeude-dialog/edit-gebaeude-dialog.component';
import { EditSektionDialogComponent } from './edit-sektion-dialog/edit-sektion-dialog.component';
import { EditRaumDialogComponent } from './edit-raum-dialog/edit-raum-dialog.component';
import { EditIndicatorDialogComponent } from './edit-indicator-dialog/edit-indicator-dialog.component';
import { environment } from '../../../environments/environment';
import { SettingsService } from '../../services/settings.service';
import { IndikatorenService } from '../../indikatoren/services/indikatoren.service';

// Inline progress dialog component for this module
@Component({
  selector: 'app-progress-dialog-inline',
  template: `
    <div class="progress-dialog-container">
      <h2 mat-dialog-title>{{ data.title || 'Bitte warten' }}</h2>
      <mat-dialog-content>
        <div class="flex flex-col items-center justify-center py-6">
          <mat-spinner diameter="50"></mat-spinner>
          <p class="mt-4 text-gray-600 text-center">{{ data.message || 'Verarbeitung läuft...' }}</p>
        </div>
      </mat-dialog-content>
    </div>
  `,
  styles: [`
    .progress-dialog-container {
      min-width: 300px;
    }
    mat-spinner {
      margin: 0 auto;
    }
    mat-dialog-content {
      padding: 0 24px;
    }
  `]
})
export class ProgressDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { title?: string; message?: string }
  ) {}
}

@Component({
  selector: 'app-objekt-struktur-treetable',
  templateUrl: './objekt-struktur-treetable.component.html',
  styleUrls: ['./objekt-struktur-treetable.component.scss']
})
export class ObjektStrukturTreetableComponent implements OnInit, OnDestroy, AfterViewChecked {
  private _standortId: string = '';

  @ViewChild('treeContainer', { static: false }) treeContainer?: ElementRef;

  treeData: any[] = [];
  loading: boolean = false;
  error: string = '';
  showIndicators: boolean = false;
  showHistory: boolean = false;
  quickCreating: boolean = false;
  private destroy$ = new Subject<void>();
  private expandedState: Map<string, boolean> = new Map<string, boolean>();
  private savedScrollPosition: number = 0;
  private shouldRestoreScroll: boolean = false;

  constructor(
    private objektStrukturService: ObjektStrukturTreetableService,
    private objektStrukturCrudService: ObjektStrukturService,
    private standortService: StandortService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private journalService: JournalService,
    private settingsService: SettingsService,
    private indikatorenService: IndikatorenService
  ) { }

  @Input() 
  set standortId(value: string) {
    console.log('ObjektStrukturTreetableComponent: standortId changed from', this._standortId, 'to', value);
    this._standortId = value;
    if (value && value !== this._standortId) {
      this.loadObjectStructure();
    }
  }
  
  get standortId(): string {
    return this._standortId;
  }

  @Output() indicatorAdded = new EventEmitter<void>();
  @Output() indicatorDeleted = new EventEmitter<void>();
  @Output() indicatorUpdated = new EventEmitter<void>();
  @Output() structureChanged = new EventEmitter<void>();

  ngOnInit(): void {
    console.log('ObjektStrukturTreetableComponent: ngOnInit called with standortId:', this.standortId);
    if (this.standortId) {
      this.loadObjectStructure();
    } else {
      console.warn('ObjektStrukturTreetableComponent: No standortId provided');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    // Restore scroll position after view is checked (DOM is ready)
    if (this.shouldRestoreScroll) {
      this.restoreScrollPosition();
      this.shouldRestoreScroll = false;
    }
  }

  /**
   * Load object structure data from backend
   */
  loadObjectStructure(): void {
    console.log('ObjektStrukturTreetableComponent: Loading object structure for standortId:', this.standortId, 'showHistory:', this.showHistory);
    
    // Save expanded state and scroll position before reloading (if treeData exists)
    if (this.treeData && this.treeData.length > 0) {
      this.saveExpandedState(this.treeData);
      this.saveScrollPosition();
    }
    
    this.loading = true;
    this.error = '';

    // Always get all items (both active and inactive) - filtering is done in frontend
    this.objektStrukturService.getObjectStructure(this.standortId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.result === 1) {
            this.treeData = this.transformToTreeFormat(response.data);
            // Mark that we should restore scroll position after view is checked
            this.shouldRestoreScroll = true;
          } else {
            this.error = response.message || 'Error loading object structure';
            console.error('ObjektStrukturTreetableComponent: API returned error:', this.error);
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('ObjektStrukturTreetableComponent: Error fetching object structure:', error);
          console.error('ObjektStrukturTreetableComponent: Error details:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            message: error.message
          });
          this.error = 'Error loading object structure';
          this.loading = false;
        }
      });
  }


  /**
   * Toggle history view
   */
  onHistoryToggle(): void {
    console.log('History toggle changed to:', this.showHistory);
    // No need to reload data - we already have all items, just toggle the display
    // The template will handle showing/hiding based on showHistory and status
  }

  /**
   * Transform backend data to tree format
   */
  private transformToTreeFormat(data: any[]): any[] {
    console.log('ObjektStrukturTreetableComponent: Transforming data:', data);
    if (!Array.isArray(data)) {
      console.warn('ObjektStrukturTreetableComponent: Data is not an array:', data);
      return [];
    }
    
    const transformed = data.map(node => this.transformNode(node));

    // Restore expanded state from saved state, or use defaults for new nodes
    this.restoreExpandedState(transformed);
    
    return transformed;
  }

  /**
   * Transform a single node to tree format
   */
  private transformNode(node: any, parentStatus: number = 1): any {
    const hasChildren = node.children && node.children.length > 0;
    // Treat null/undefined as active (default behavior - some items might not have status field)
    const nodeStatus = node.status ?? 1;
    
    // Normalize status values to numbers (1 for active, 0 for inactive)
    // null/undefined/1/true = active, 0/false = inactive
    const normalizedNodeStatus = (nodeStatus === null || nodeStatus === undefined || nodeStatus === 1 || nodeStatus === true) ? 1 : 0;
    const normalizedParentStatus = parentStatus === 1 ? 1 : 0;
    
    // Check if this node is effectively active (node is active AND parent is active)
    const isEffectivelyActive = normalizedNodeStatus === 1 && normalizedParentStatus === 1;
    
    // Transform children, passing the effective parent status
    // If this node is effectively active, children get parentStatus = 1
    // If this node is not effectively active (either inactive or has inactive parent), children get parentStatus = 0
    const effectiveParentStatus = isEffectivelyActive ? 1 : 0;
    let transformedChildren = hasChildren ? node.children.map((child: any) => this.transformNode(child, effectiveParentStatus)) : [];
    
    // If children are indicators (type === 'indicator'), sort them by indikator_number
    if (transformedChildren.length > 0 && transformedChildren[0]?.data?.type === 'indicator') {
      transformedChildren = this.sortIndicatorsByNumber(transformedChildren);
    }
    
             return {
               data: {
                 id: node.id || node.name,
                 name: node.name || 'Unnamed',
                 type: node.type || 'unknown',
                 description: node.description || '',
                 indikator_name: node.indikator_name || '',
                 indikator_type: node.indikator_type || '', // This is the indikator code
                 indikator_number: node.indikator_number || null,
                 schwellenwert: node.schwellenwert || '',
                 value: node.value || '',
                 symbol: node.symbol || '', // This is the symbol from indikator
                 color: node.color || '',
                 notes: node.notes || '',
                 standort_id: node.standort_id || '',
                 einheit: node.einheit || '',
                 mehr_weniger: node.mehr_weniger || '',
                 qr_code: node.qr_code || '',
                 status: nodeStatus, // Add status field
                 isEffectivelyActive: isEffectivelyActive, // Track if node and all parents are active
                 activated_at: node.activated_at || null,
                 deactivated_at: node.deactivated_at || null
               },
               children: transformedChildren,
               expanded: false,
               leaf: !hasChildren
             };
  }

  /**
   * Check if a node should be visible based on history toggle and status
   * When showHistory is false: show active items (Gebäude, Sektionen, Räume) - just check their own status
   * When showHistory is true: show all items regardless of status
   * For indicators: always check parent chain (only show if effectively active)
   */
  hasActiveParentChain(node: any): boolean {
    const nodeStatus = node.data.status;
    const nodeType = node.data.type;
    
    // When showHistory is true: show everything (active and inactive)
    if (this.showHistory) {
      return true;
    }
    
    // When showHistory is false:
    // - For Gebäude, Sektionen, Räume: show if active (status === 1 or null/undefined, which defaults to active)
    // - For indicators: show if effectively active (active AND all parents active)
    
    if (nodeType === 'indicator') {
      // Indicators: only show if effectively active
      // Normalize status: null/undefined/1/true = active, 0/false = inactive
      const normalizedStatus = (nodeStatus === null || nodeStatus === undefined || nodeStatus === 1 || nodeStatus === true) ? 1 : 0;
      if (normalizedStatus !== 1) {
        return false; // Hide inactive indicators
      }
      // Check parent chain - only show if all parents are active
      return node.data.isEffectivelyActive !== false;
    }
    
    // For Gebäude, Sektionen, Räume: just check their own status
    // Treat null/undefined as active (default behavior - rooms might not have status field)
    // Don't check parent chain for visibility - just show if active or null
    const normalizedStatus = (nodeStatus === null || nodeStatus === undefined || nodeStatus === 1 || nodeStatus === true) ? 1 : 0;
    return normalizedStatus === 1;
  }

  /**
   * Count active indikatoren for a room node
   * When showHistory is false: count only active indikatoren
   * When showHistory is true: count all indikatoren
   */
  getActiveIndicatorCount(roomNode: any): number {
    if (!roomNode || !roomNode.children || roomNode.children.length === 0) {
      return 0;
    }
    
    // When showHistory is true: count all indikatoren
    if (this.showHistory) {
      return roomNode.children.length;
    }
    
    // When showHistory is false: count only active indikatoren
    return roomNode.children.filter((indicator: any) => {
      const nodeStatus = indicator.data.status;
      // Normalize status: null/undefined/1/true = active, 0/false = inactive
      const normalizedStatus = (nodeStatus === null || nodeStatus === undefined || nodeStatus === 1 || nodeStatus === true) ? 1 : 0;
      if (normalizedStatus !== 1) {
        return false; // Exclude inactive indicators
      }
      // Check parent chain - only count if effectively active
      return indicator.data.isEffectivelyActive !== false;
    }).length;
  }

  /**
   * Format date to German format (DD.MM.YYYY) - date only, no time
   */
  formatDateOnly(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Sort indicators by indikator_number
   */
  private sortIndicatorsByNumber(indicators: any[]): any[] {
    return [...indicators].sort((a, b) => {
      const numA = a.data?.indikator_number ?? 999999;
      const numB = b.data?.indikator_number ?? 999999;
      return numA - numB;
    });
  }

  /**
   * Get node icon based on type
   */
  getNodeIcon(type: string): string {
    switch (type) {
      case 'gebaeude':
        return 'business';
      case 'sektion':
        return 'folder';
      case 'room':
        return 'home';
      case 'indicator':
        return 'trending_up';
      default:
        return 'help';
    }
  }

  /**
   * Get type label for display
   */
  getTypeLabel(type: string): string {
    switch (type) {
      case 'gebaeude':
        return 'Gebäude';
      case 'sektion':
        return 'Sektion';
      case 'room':
        return 'Raum';
      case 'indicator':
        return 'Indikator';
      default:
        return type;
    }
  }

  /**
   * Get color class for indicator status
   */
  getColorClass(color: string): string {
    switch (color) {
      case 'green':
        return 'text-green-600';
      case 'orange':
        return 'text-orange-600';
      case 'red':
        return 'text-red-600';
      case 'yellow':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Get background color class for indicator status
   */
  getBackgroundColorClass(color: string): string {
    switch (color) {
      case 'green':
        return 'bg-green-100';
      case 'orange':
        return 'bg-orange-100';
      case 'red':
        return 'bg-red-100';
      case 'yellow':
        return 'bg-yellow-100';
      default:
        return 'bg-gray-100';
    }
  }

  /**
   * Toggle node expansion
   */
  toggleNode(node: any): void {
    node.expanded = !node.expanded;
  }

  /**
   * Toggle visibility of all indicator tables
   */
  toggleShowIndicators(): void {
    this.showIndicators = !this.showIndicators;
    console.log('Toggle showIndicators to:', this.showIndicators);
  }

  /**
   * Add indicator to a room
   */
  addIndicator(roomId: string, roomName: string): void {
    console.log('Adding indicator to room:', roomId, roomName);
    
    const dialogRef = this.dialog.open(AddIndicatorDialogComponent, {
      width: '450px',
      data: { 
        roomId: roomId, 
        standortId: this.standortId 
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new indicators:', result.data);
        
        const anzahl = result.data.anzahl || 1;
        const baseName = result.data.name;
        
        // Create multiple indicators based on anzahl
        this.createMultipleIndicators(result.data, anzahl, baseName);
      }
    });
  }

  /**
   * Create multiple indicators sequentially
   */
  private createMultipleIndicators(data: any, anzahl: number, baseName: string): void {
    let createdCount = 0;
    let hasError = false;

    const createNextIndicator = (index: number) => {
      if (index > anzahl) {
        // All indicators created
        if (!hasError) {
          console.log(`Successfully created ${anzahl} indicators`);
          this.loadObjectStructure();
          this.indicatorAdded.emit();
        } else {
          alert(`Fehler beim Erstellen der Indikatoren. ${createdCount} von ${anzahl} Indikatoren wurden erstellt.`);
        }
        return;
      }

      const createData = {
        room_id: data.room_id,
        indikator_id: data.indikator_id,
        name: anzahl > 1 ? `${baseName} ${index.toString().padStart(2, '0')}` : baseName,
        schwellenwert: data.schwellenwert,
        einheit: data.einheit,
        mehr_weniger: 'mehr',
        current_value: '0',
        status: 1,
        qr_code: '',
        position_x: null,
        position_y: null,
        bemerkung: '',
        created_by: null
      };

      this.objektStrukturCrudService.createIndicator(createData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log(`Indicator ${index} created successfully:`, response);
            createdCount++;
            createNextIndicator(index + 1);
          },
          error: (error) => {
            console.error(`Error creating indicator ${index}:`, error);
            hasError = true;
            createNextIndicator(index + 1);
          }
        });
    };

    createNextIndicator(1);
  }

  /**
   * Edit existing indicator
   */
  editIndicator(indicatorId: string, indicatorName: string): void {
    // Strip the 'ind_' prefix from the indicator ID to get the actual UUID
    const actualIndicatorId = indicatorId.replace('ind_', '');
    
    // First, get the current indicator data and settings
    this.objektStrukturCrudService.getRoomIndikatorById(actualIndicatorId).subscribe({
      next: (indicatorData) => {
        // Load only the required data
        this.objektStrukturCrudService.getIndikatoren().subscribe(indikatoren => {
          const dialogRef = this.dialog.open(EditIndicatorDialogComponent, {
            width: '600px',
            data: {
              id: indicatorId,
              roomId: indicatorData.room_id,
              standortId: this.standortId,
              currentData: indicatorData,
              indikatorOptions: indikatoren || []
            },
            panelClass: 'custom-dialog-container'
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result && result.event === 'update') {
              this.objektStrukturCrudService.updateRoomIndikator(actualIndicatorId, result.data)
                .subscribe({
                  next: (response) => {
                    console.log('Indicator updated:', response);
                    this.loadObjectStructure();
                    this.indicatorUpdated.emit();
                    this.structureChanged.emit();
                  },
                  error: (error) => {
                    console.error('Error updating indicator:', error);
                  }
                });
            } else if (result && result.event === 'delete') {
              this.objektStrukturCrudService.deleteRoomIndikator(actualIndicatorId)
                .subscribe({
                  next: (response) => {
                    console.log('Indicator deleted:', response);
                    this.loadObjectStructure();
                    this.indicatorDeleted.emit();
                    this.structureChanged.emit();
                  },
                  error: (error) => {
                    console.error('Error deleting indicator:', error);
                  }
                });
            }
          });
        });
      },
      error: (error) => {
        console.error('Error loading indicator data:', error);
      }
    });
  }

  /**
   * Add new section
   */
  addSection(gebaeudeId: string): void {
    console.log('Adding new section for building:', gebaeudeId);
    
    const dialogRef = this.dialog.open(AddSektionDialogComponent, {
      width: '450px',
      data: { 
        gebaeudeId: gebaeudeId
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new section:', result.data);
        
        this.objektStrukturCrudService.createSektion(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Section created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create section:', response.message);
                alert('Fehler beim Erstellen der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating section:', error);
              alert('Fehler beim Erstellen der Sektion');
            }
          });
      }
    });
  }

  /**
   * Add new room to a section
   */
  addRoom(sectionId: string, sectionName: string): void {
    console.log('Adding new room to section:', sectionId, sectionName);
    
    const dialogRef = this.dialog.open(AddRaumDialogComponent, {
      width: '450px',
      data: { 
        sektionId: sectionId
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new room:', result.data);
        
        this.objektStrukturCrudService.createRaum(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Room created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create room:', response.message);
                alert('Fehler beim Erstellen des Raums: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating room:', error);
              alert('Fehler beim Erstellen des Raums');
            }
          });
      }
    });
  }

  /**
   * Add new building
   */
  addGebaeude(): void {
    console.log('Adding new building for standort:', this.standortId);
    
    const dialogRef = this.dialog.open(AddGebaeudeDialogComponent, {
      width: '450px',
      data: { standortId: this.standortId },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        console.log('Creating new building:', result.data);
        
        this.objektStrukturCrudService.createGebaeude(result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Building created successfully:', response.data);
                // Refresh the object structure
                this.loadObjectStructure();
              } else {
                console.error('Failed to create building:', response.message);
                alert('Fehler beim Erstellen des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error creating building:', error);
              alert('Fehler beim Erstellen des Gebäudes');
            }
          });
      }
    });
  }

  /**
   * Quick-Anlage: create a full default Objekt-Struktur (Gebäude > Sektion > Raum)
   * in one step, using the default names configured in the environment settings.
   */
  quickCreateStructure(): void {
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }
    if (this.quickCreating) {
      return;
    }

    this.settingsService.getSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings: any) => {
          const envDefaults = (environment as any).objektStrukturDefaults || {};
          const defaults = settings?.objektStrukturDefaults || envDefaults;
          const defaultIndikatorId = settings?.defaultIndikatorId ?? null;
          this.runQuickCreateStructure(defaults, defaultIndikatorId);
        },
        error: () => {
          const defaults = (environment as any).objektStrukturDefaults || {};
          this.runQuickCreateStructure(defaults, null);
        }
      });
  }

  private runQuickCreateStructure(defaults: { gebaeude?: string; sektion?: string; raum?: string }, defaultIndikatorId: number | null): void {
    const gebaeudeName = defaults.gebaeude || 'Hauptgebäude';
    const sektionName = defaults.sektion || 'Erdgeschoss';
    const raumName = defaults.raum || 'Hauptraum';

    this.quickCreating = true;
    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '300px',
      disableClose: true,
      data: {
        title: 'Quick-Anlage',
        message: 'Gebäude, Sektion und Raum werden erstellt...'
      }
    });

    const finish = (success: boolean, message: string) => {
      this.quickCreating = false;
      progressDialogRef.close();
      this.snackBar.open(message, 'Schließen', { duration: success ? 3000 : 5000 });
      if (success) {
        this.loadObjectStructure();
        this.structureChanged.emit();
      }
    };

    const createDefaultIndikator = (roomId: string, onComplete: (success: boolean, message: string) => void) => {
      if (!defaultIndikatorId) {
        onComplete(true, 'Objekt-Struktur erfolgreich angelegt');
        return;
      }

      this.indikatorenService.getIndikator(defaultIndikatorId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (masterIndikator) => {
            const schwellenwert = masterIndikator.schwellenwert != null
              ? String(masterIndikator.schwellenwert)
              : 'Standard';
            this.objektStrukturCrudService.createIndicator({
              room_id: roomId,
              indikator_id: String(masterIndikator.id),
              name: masterIndikator.code,
              schwellenwert,
              einheit: masterIndikator.einheit || '°C',
              mehr_weniger: 'mehr',
              current_value: '0',
              status: 1,
            }).pipe(takeUntil(this.destroy$)).subscribe({
              next: () => onComplete(true, 'Objekt-Struktur mit Standard-Indikator erfolgreich angelegt'),
              error: (error) => {
                console.error('Error creating default indikator (quick):', error);
                onComplete(true, 'Objekt-Struktur angelegt, Standard-Indikator konnte nicht erstellt werden');
              }
            });
          },
          error: (error) => {
            console.error('Error loading default indikator (quick):', error);
            onComplete(true, 'Objekt-Struktur angelegt, Standard-Indikator konnte nicht geladen werden');
          }
        });
    };

    this.objektStrukturCrudService.createGebaeude({ name: gebaeudeName, standort_id: this.standortId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (gebaeudeResponse) => {
          if (!gebaeudeResponse.success || !gebaeudeResponse.data?.id) {
            finish(false, 'Fehler beim Erstellen des Gebäudes: ' + (gebaeudeResponse.message || ''));
            return;
          }
          const gebaeudeId = gebaeudeResponse.data.id;

          this.objektStrukturCrudService.createSektion({ name: sektionName, gebaeude_id: gebaeudeId })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (sektionResponse) => {
                if (!sektionResponse.success || !sektionResponse.data?.id) {
                  finish(false, 'Fehler beim Erstellen der Sektion: ' + (sektionResponse.message || ''));
                  return;
                }
                const sektionId = sektionResponse.data.id;

                this.objektStrukturCrudService.createRaum({ name: raumName, sektion_id: sektionId })
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: (raumResponse) => {
                      if (!raumResponse.success || !raumResponse.data?.id) {
                        finish(false, 'Fehler beim Erstellen des Raums: ' + (raumResponse.message || ''));
                        return;
                      }
                      const roomId = raumResponse.data.id;
                      const entityIds = this.journalService.extractEntityIds({ standort_id: this.standortId });
                      this.journalService.log(
                        'Quick-Anlage Objekt-Struktur',
                        this.standortId,
                        `Gebäude: ${gebaeudeName}, Sektion: ${sektionName}, Raum: ${raumName}`,
                        entityIds
                      );
                      createDefaultIndikator(roomId, finish);
                    },
                    error: (error) => {
                      console.error('Error creating room (quick):', error);
                      finish(false, 'Fehler beim Erstellen des Raums');
                    }
                  });
              },
              error: (error) => {
                console.error('Error creating section (quick):', error);
                finish(false, 'Fehler beim Erstellen der Sektion');
              }
            });
        },
        error: (error) => {
          console.error('Error creating building (quick):', error);
          finish(false, 'Fehler beim Erstellen des Gebäudes');
        }
      });
  }

  /**
   * Edit building
   */
  editGebaeude(id: string, name: string): void {
    console.log('Editing building:', id, name);
    
    // Find the node in treeData to get status
    let status = 1; // Default to active
    for (const node of this.treeData) {
      if (node.data.id === id) {
        status = node.data.status ?? 1;
        break;
      }
    }
    
    const dialogRef = this.dialog.open(EditGebaeudeDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        standortId: this.standortId,
        status: status
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating building:', result.data);
        
        this.objektStrukturCrudService.updateGebaeude(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Building updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update building:', response.message);
                alert('Fehler beim Aktualisieren des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error updating building:', error);
              alert('Fehler beim Aktualisieren des Gebäudes');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting building:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteGebaeude(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteGebaeude - Response received:', response);
              if (response.success) {
                console.log('Building deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Das Gebäude wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete building:', response.message);
                alert('Fehler beim Löschen des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting building:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Building is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen des Gebäudes');
              }
            }
          });
      } else if (result && result.event === 'restore') {
        console.log('Restoring building:', result.data.id);
        
        // Get current name from form or data
        const currentName = name;
        
        this.objektStrukturCrudService.updateGebaeude(result.data.id, { name: currentName, status: 1 })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Building restored successfully:', response.data);
                // Create journal entry
                const entityIds = this.journalService.extractEntityIds({
                  standort_id: this.standortId
                });
                this.journalService.log(
                  'Gebäude wiederhergestellt',
                  this.standortId,
                  `Gebäude: ${currentName} (ID: ${result.data.id})`,
                  entityIds
                );
                this.loadObjectStructure(); // Refresh the object structure
                this.snackBar.open('Gebäude erfolgreich wiederhergestellt', 'Schließen', { duration: 3000 });
              } else {
                console.error('Failed to restore building:', response.message);
                alert('Fehler beim Wiederherstellen des Gebäudes: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error restoring building:', error);
              const errorMsg = error.error?.message || error.error?.error || 'Fehler beim Wiederherstellen des Gebäudes';
              alert(errorMsg);
            }
          });
      }
    });
  }

  /**
   * Edit section
   */
  editSection(id: string, name: string): void {
    console.log('Editing section:', id, name);
    
    // Find the parent building to get gebaeude_id and get status
    let gebaeudeId = '';
    let status = 1; // Default to active
    for (const node of this.treeData) {
      for (const child of node.children || []) {
        if (child.data.id === id) {
          gebaeudeId = node.data.id;
          status = child.data.status ?? 1;
          break;
        }
      }
      if (gebaeudeId) break;
    }
    
    const dialogRef = this.dialog.open(EditSektionDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        gebaeudeId: gebaeudeId,
        status: status
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating section:', result.data);
        
        this.objektStrukturCrudService.updateSektion(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Section updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update section:', response.message);
                alert('Fehler beim Aktualisieren der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error updating section:', error);
              alert('Fehler beim Aktualisieren der Sektion');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting section:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteSektion(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteSektion - Response received:', response);
              if (response.success) {
                console.log('Section deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Die Sektion wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete section:', response.message);
                alert('Fehler beim Löschen der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting section:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Section is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen der Sektion');
              }
            }
          });
      } else if (result && result.event === 'restore') {
        console.log('Restoring section:', result.data.id);
        
        // Get current name from form or data
        const currentName = name;
        
        this.objektStrukturCrudService.updateSektion(result.data.id, { name: currentName, status: 1 })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Section restored successfully:', response.data);
                // Create journal entry
                const entityIds = this.journalService.extractEntityIds({
                  standort_id: this.standortId
                });
                this.journalService.log(
                  'Sektion wiederhergestellt',
                  this.standortId,
                  `Sektion: ${currentName} (ID: ${result.data.id})`,
                  entityIds
                );
                this.loadObjectStructure(); // Refresh the object structure
                this.snackBar.open('Sektion erfolgreich wiederhergestellt', 'Schließen', { duration: 3000 });
              } else {
                console.error('Failed to restore section:', response.message);
                alert('Fehler beim Wiederherstellen der Sektion: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error restoring section:', error);
              const errorMsg = error.error?.message || error.error?.error || 'Fehler beim Wiederherstellen der Sektion';
              alert(errorMsg);
            }
          });
      }
    });
  }

  /**
   * Edit room
   */
  editRoom(id: string, name: string): void {
    console.log('Editing room:', id, name);
    
    // Find the parent section to get sektion_id and get status
    let sektionId = '';
    let status = 1; // Default to active
    for (const node of this.treeData) {
      for (const child of node.children || []) {
        for (const grandchild of child.children || []) {
          if (grandchild.data.id === id) {
            sektionId = child.data.id;
            status = grandchild.data.status ?? 1;
            break;
          }
        }
        if (sektionId) break;
      }
      if (sektionId) break;
    }
    
    const dialogRef = this.dialog.open(EditRaumDialogComponent, {
      width: '450px',
      data: { 
        id: id, 
        name: name, 
        sektionId: sektionId,
        status: status
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'update') {
        console.log('Updating room:', result.data);
        
        this.objektStrukturCrudService.updateRaum(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Room updated successfully:', response.data);
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to update room:', response.message);
                alert('Fehler beim Aktualisieren des Raums: ' + response.message);
            }
            },
            error: (error) => {
              console.error('Error updating room:', error);
              alert('Fehler beim Aktualisieren des Raums');
            }
          });
      } else if (result && result.event === 'delete') {
        console.log('Soft deleting room:', result.data.id);
        
        this.objektStrukturCrudService.softDeleteRaum(result.data.id, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              console.log('SoftDeleteRaum - Response received:', response);
              if (response.success) {
                console.log('Room deleted successfully, status in response:', response.data?.status);
                
                // Check if the backend actually updated the status
                if (response.data?.status === 1) {
                  console.warn('Backend did not update status to inactive. Status is still:', response.data.status);
                  alert('Warnung: Der Raum wurde möglicherweise nicht als inaktiv markiert. Bitte überprüfen Sie den Status.');
                }
                
                this.loadObjectStructure(); // Refresh the object structure
              } else {
                console.error('Failed to delete room:', response.message);
                alert('Fehler beim Löschen des Raums: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error deleting room:', error);
              
              // Check if the error is "already inactive"
              if (error.error && error.error.message && error.error.message.includes('already inactive')) {
                console.log('Room is already inactive, refreshing structure to show updated status');
                this.loadObjectStructure(); // Refresh to show the inactive status
              } else {
                alert('Fehler beim Löschen des Raums');
              }
            }
          });
      } else if (result && result.event === 'restore') {
        console.log('Restoring room:', result.data.id);
        
        // Get current name from form or data
        const currentName = name;
        
        this.objektStrukturCrudService.updateRaum(result.data.id, { name: currentName, status: 1 })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success) {
                console.log('Room restored successfully:', response.data);
                // Create journal entry with entity IDs
                const entityIds = this.journalService.extractEntityIds({
                  standort_id: this.standortId
                });
                this.journalService.log(
                  'Raum wiederhergestellt',
                  this.standortId,
                  `Raum: ${currentName} (ID: ${result.data.id})`,
                  entityIds
                );
                this.loadObjectStructure(); // Refresh the object structure
                this.snackBar.open('Raum erfolgreich wiederhergestellt', 'Schließen', { duration: 3000 });
              } else {
                console.error('Failed to restore room:', response.message);
                alert('Fehler beim Wiederherstellen des Raums: ' + response.message);
              }
            },
            error: (error) => {
              console.error('Error restoring room:', error);
              const errorMsg = error.error?.message || error.error?.error || 'Fehler beim Wiederherstellen des Raums';
              alert(errorMsg);
            }
          });
      }
    });
  }

  /**
   * Print QR Labels for all objects in the structure
   */
  printQRLabels(): void {
    console.log('Printing QR labels for standort:', this.standortId);
    
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }
    
    // Open progress dialog
    const progressDialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '300px',
      disableClose: true, // Prevent closing during operation
      data: {
        title: 'QR Labels werden generiert',
        message: 'Bitte warten Sie, während die PDF generiert wird...'
      }
    });
    
    console.log('DEBUG: About to call generateQRIndicatorLabelsPDF');
    this.standortService.generateQRIndicatorLabelsPDF(this.standortId).subscribe({
      next: (pdfBlob: Blob) => {
        // Close progress dialog
        progressDialogRef.close();
        
        // Generate and download PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-labels-standort-${this.standortId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open('QR Labels PDF wird heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        // Close progress dialog on error
        progressDialogRef.close();
        
        console.error('Error generating PDF:', error);
        this.snackBar.open('Fehler beim Generieren der PDF', 'Schließen', { duration: 5000 });
      }
    });
  }

  /**
   * Save expanded state of all nodes recursively
   */
  private saveExpandedState(treeData: any[]): void {
    this.expandedState.clear();
    this.collectExpandedState(treeData);
  }

  /**
   * Recursively collect expanded state from tree nodes
   */
  private collectExpandedState(nodes: any[]): void {
    if (!nodes || !Array.isArray(nodes)) {
      return;
    }

    nodes.forEach(node => {
      if (node.data && node.data.id) {
        this.expandedState.set(node.data.id, node.expanded === true);
      }

      if (node.children && node.children.length > 0) {
        this.collectExpandedState(node.children);
      }
    });
  }

  /**
   * Restore expanded state from saved state, applying defaults for new nodes
   */
  private restoreExpandedState(treeData: any[]): void {
    if (this.expandedState.size === 0) {
      // No saved state, use default expansion
      this.expandAllNodes(treeData);
      return;
    }

    this.applyExpandedState(treeData);
  }

  /**
   * Recursively apply expanded state to tree nodes
   */
  private applyExpandedState(nodes: any[]): void {
    if (!nodes || !Array.isArray(nodes)) {
      return;
    }

    nodes.forEach(node => {
      if (node.data && node.data.id) {
        const savedState = this.expandedState.get(node.data.id);
        if (savedState !== undefined) {
          // Restore saved state
          node.expanded = savedState;
        } else {
          // New node - apply default expansion based on type
          node.expanded = this.getDefaultExpansion(node.data.type);
        }
      } else {
        // No ID - apply default expansion
        node.expanded = this.getDefaultExpansion(node.data?.type);
      }

      if (node.children && node.children.length > 0) {
        this.applyExpandedState(node.children);
      }
    });
  }

  /**
   * Get default expansion state based on node type
   */
  private getDefaultExpansion(nodeType: string): boolean {
    switch (nodeType) {
      case 'gebaeude':
        return true;
      case 'sektion':
        return true;
      case 'room':
        return false;
      case 'indicator':
        return false;
      default:
        return false;
    }
  }

  /**
   * Save current scroll position
   */
  private saveScrollPosition(): void {
    // Try to get scroll position from window (most common case)
    if (typeof window !== 'undefined') {
      this.savedScrollPosition = window.pageYOffset || window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }
    
    // Also try to get scroll position from container element if available
    if (this.treeContainer && this.treeContainer.nativeElement) {
      const containerScroll = this.treeContainer.nativeElement.scrollTop;
      if (containerScroll > 0) {
        this.savedScrollPosition = containerScroll;
      }
    }
  }

  /**
   * Restore saved scroll position
   */
  private restoreScrollPosition(): void {
    if (this.savedScrollPosition === 0) {
      return; // No saved position or already at top
    }

    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      // Try to restore scroll position in container element first
      if (this.treeContainer && this.treeContainer.nativeElement) {
        this.treeContainer.nativeElement.scrollTop = this.savedScrollPosition;
      }
      
      // Also restore window scroll position
      if (typeof window !== 'undefined') {
        window.scrollTo(0, this.savedScrollPosition);
      }
    }, 0);
  }

  /**
   * Expand all tree nodes by default (Gebäude, Sektionen, and Räume)
   * Keep indicators table collapsed
   */
  private expandAllNodes(treeData: any[]): void {
    treeData.forEach(node => {
      // Level 1: Gebäude
      node.expanded = true;
      
      if (node.children) {
        node.children.forEach(child => {
          // Level 2: Sektionen
          child.expanded = true;
          
          if (child.children) {
            child.children.forEach(grandchild => {
              // Level 3: Räume
              grandchild.expanded = false;
              
              // Level 4: Indicators - keep collapsed
              if (grandchild.children) {
                grandchild.children.forEach(indicator => {
                  indicator.expanded = false; // Keep indicators collapsed
                });
              }
            });
          }
        });
      }
    });
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  /**
   * Get symbol URL from environment mapping
   */
  getSymbolUrl(symbolId?: string | null): string | null {
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  /**
   * Get symbol color from environment mapping
   */
  getSymbolColor(symbolId?: string | null): string | null {
    if (!symbolId) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[symbolId] || null;
  }

  /**
   * Format schwellenwert as integer (remove decimal places)
   */
  formatSchwellenwertAsInteger(schwellenwert: any): string {
    if (schwellenwert === null || schwellenwert === undefined || schwellenwert === '') {
      return '-';
    }
    
    // Convert to number and round to nearest integer
    const numValue = parseFloat(schwellenwert);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      return schwellenwert.toString(); // Return original if not a number
    }
    
    // Round to nearest integer and return as string
    return Math.round(numValue).toString();
  }

  /**
   * Get status text for room indicator
   */
  getStatusText(status: number | boolean): string {
    if (status === 1 || status === true) {
      return 'Aktiv';
    }
    return 'Inaktiv';
  }

  /**
   * Get status classes for styling
   */
  getStatusClasses(status: number | boolean): string {
    if (status === 1 || status === true) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }
}
